import { updateElementsOrderFromZIndex } from '@/utils/elementsUtils';
import type { Object as FabricObject } from 'fabric/fabric-impl';
import type { CanvasEx } from '@/typings/canvas';
import { 
    type PatternConfig, 
    PATTERN_DEFAULTS, 
    type PatternCell, 
    type CellPosition, 
    type PatternElement, 
    type CreateTextPatternHandlersParams 
} from '@/typings/customilyElements/patternElements';


/** Parse the stored patternConfig (JSON string or object) into a full config, or null. */
export const parsePatternConfig = (value: string | PatternConfig | null): PatternConfig | null => {
    if (!value) return null;
    try {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        if (!parsed || typeof parsed !== 'object') return null;
        return { ...PATTERN_DEFAULTS, ...(parsed as Partial<PatternConfig>) };
    } catch {
        return null;
    }
};

const halfExtent = (total: number): number => Math.max(0, Math.floor((total - 1) / 2));

/**
 * Enumerate the grid cells for a config in a deterministic, stable order,
 * excluding the origin cell (0,0). The order must match between creation and re-sync so replicas map
 * to the same cell across operations.
 */
export const enumerateCells = (config: PatternConfig): PatternCell[] => {
    const rowExtent = halfExtent(config.rows);
    const colExtent = halfExtent(config.cols);
    const cells: PatternCell[] = [];
    for (let row = -rowExtent; row <= rowExtent; row++) {
        for (let col = -colExtent; col <= colExtent; col++) {
            if (row === 0 && col === 0) continue;
            cells.push({ row, col });
        }
    }
    return cells;
};

/** Center-to-center step between adjacent cells and the origin's on-canvas size, in pixels. */
const getGridSteps = (
    origin: FabricObject,
    config: PatternConfig
): { stepX: number; stepY: number; width: number; height: number } => {
    const width = origin.getScaledWidth();
    const height = origin.getScaledHeight();
    return {
        width,
        height,
        stepX: width * (1 + config.spacingX),
        stepY: height * (1 + config.spacingY)
    };
};

/** Absolute canvas center position for a single cell. */
export const computeCellPosition = (origin: FabricObject, config: PatternConfig, cell: PatternCell): CellPosition => {
    const { stepX, stepY, width, height } = getGridSteps(origin, config);
    const center = origin.getCenterPoint();
    const oddRowShift = cell.row % 2 !== 0 ? config.offsetX * width : 0;
    const oddColShift = cell.col % 2 !== 0 ? config.offsetY * height : 0;
    return {
        ...cell,
        left: center.x + cell.col * stepX + oddRowShift,
        top: center.y + cell.row * stepY + oddColShift
    };
};

export const getArtworkBounds = (canvas: CanvasEx): { width: number; height: number } => ({
    width: (canvas.getArtworkWidth() / canvas.getZoom()) * canvas.getEffectiveZoom(),
    height: (canvas.getArtworkHeight() / canvas.getZoom()) * canvas.getEffectiveZoom()
});

/**
 * Compute how many rows/cols are needed to fill the artwork: expand outward from the centered origin
 * while a cell still intersects the canvas at all; stop once the next ring is fully out of bounds.
 * A cell may cross the edge but is included as long as any part is inside.
 */
export const computeFillExtent = (
    origin: FabricObject,
    config: PatternConfig,
    canvasWidth: number,
    canvasHeight: number
): { rows: number; cols: number } => {
    const { stepX, stepY, width, height } = getGridSteps(origin, config);
    const center = origin.getCenterPoint();
    // Offsets stagger odd rows/cols by a fraction of the origin size; widen the half-extent by the
    // largest possible shift so the extent still covers the staggered cells and leaves no edge gaps.
    const maxShiftX = Math.abs(config.offsetX) * width;
    const maxShiftY = Math.abs(config.offsetY) * height;
    const halfW = width / 2 + maxShiftX;
    const halfH = height / 2 + maxShiftY;

    const intersectsX = (col: number) => {
        const centerX = center.x + col * stepX;
        return centerX + halfW > 0 && centerX - halfW < canvasWidth;
    };
    const intersectsY = (row: number) => {
        const centerY = center.y + row * stepY;
        return centerY + halfH > 0 && centerY - halfH < canvasHeight;
    };

    let colExtent = 0;
    if (stepX > 0) {
        while (intersectsX(colExtent + 1) || intersectsX(-(colExtent + 1))) colExtent++;
    }
    let rowExtent = 0;
    if (stepY > 0) {
        while (intersectsY(rowExtent + 1) || intersectsY(-(rowExtent + 1))) rowExtent++;
    }

    return { rows: rowExtent * 2 + 1, cols: colExtent * 2 + 1 };
};

export const createTextPatternHandlers = ({
    getCanvas,
    $scope,
    TextService,
    ProductService
}: CreateTextPatternHandlersParams) => {
    const markEdited = (origin: PatternElement, keys: string[]) => {
        if ($scope.editMode) origin.setEditedProp(...keys);
    };

    const getMinZIndex = (canvas: CanvasEx) => {
        const zIndices = canvas.getObjects().map((o) => o.zIndex);
        return zIndices.length ? Math.min(...zIndices) : 0;
    };

    const renumberReplicaZIndices = (origin: PatternElement) => {
        const canvas = getCanvas();
        const replicas = TextService.getPatternReplicaObjects(canvas, origin.uuid as string);
        replicas.forEach((replica, i) => replica.set('zIndex', origin.zIndex + 1 + i));
    };

    const createReplicasForCells = (
        origin: PatternElement,
        config: PatternConfig,
        cells: PatternCell[],
        reuseUuids: (string | undefined)[]
    ) => {
        const canvas = getCanvas();
        cells.forEach((cell, i) => {
            const pos = computeCellPosition(origin, config, cell);
            const replica = TextService.buildPatternReplica(canvas, origin, cell, {
                uuid: reuseUuids[i],
                left: pos.left,
                top: pos.top,
                zIndex: origin.zIndex + 1,
                editMode: $scope.editMode
            });
            ProductService.addPatternElementsLink(origin.uuid as string, replica.uuid as string);
        });
    };

    const reconcileReplicas = async (origin: PatternElement, config: PatternConfig, cells: PatternCell[]) => {
        const canvas = getCanvas();
        const replicas = TextService.getPatternReplicaObjects(canvas, origin.uuid as string);
        const shared = Math.min(cells.length, replicas.length);
        const tasks = [];
        for (let i = 0; i < shared; i++) {
            const pos = computeCellPosition(origin, config, cells[i]);
            replicas[i].patternCell = { row: cells[i].row, col: cells[i].col };
            tasks.push(TextService.syncPatternReplica(canvas, origin, replicas[i], pos));
        }
        await Promise.all(tasks);
        if (cells.length > replicas.length) {
            createReplicasForCells(origin, config, cells.slice(replicas.length), []);
        } else if (replicas.length > cells.length) {
            for (const extra of replicas.slice(cells.length)) {
                ProductService.removePatternElementsLinkByReplica(extra.uuid as string);
                canvas.remove(extra);
            }
        }
        renumberReplicaZIndices(origin);
    };

    const centerOrigin = (canvas: CanvasEx, origin: PatternElement) => {
        const center = canvas.getCenter();
        origin.set({ left: center.left, top: center.top });
        origin.setCoords();
    };

    const createPattern = (origin: PatternElement) => {
        const canvas = getCanvas();
        centerOrigin(canvas, origin);

        const config: PatternConfig = { ...PATTERN_DEFAULTS };
        const bounds = getArtworkBounds(canvas);
        const extent = computeFillExtent(origin, config, bounds.width, bounds.height);
        config.rows = extent.rows;
        config.cols = extent.cols;

        origin.set('zIndex', getMinZIndex(canvas) - 1);
        origin.set('pattern', true);
        origin.set('patternConfig', config);
        markEdited(origin, ['pattern', 'patternConfig', 'zIndex']);

        createReplicasForCells(origin, config, enumerateCells(config), []);
        renumberReplicaZIndices(origin);
        updateElementsOrderFromZIndex(canvas);
        canvas.setActiveObject(origin);
        canvas.requestRenderAll();
    };

    const repositionReplicasNow = (origin: PatternElement, resize: boolean) => {
        const canvas = getCanvas();
        const config = origin.patternConfig;
        if (!config) return;

        const cells = enumerateCells(config);
        const replicas = TextService.getPatternReplicaObjects(canvas, origin.uuid as string);
        const count = Math.min(cells.length, replicas.length);
        for (let i = 0; i < count; i++) {
            const pos = computeCellPosition(origin, config, cells[i]);
            replicas[i].patternCell = { row: cells[i].row, col: cells[i].col };
            TextService.repositionPatternReplica(origin, replicas[i], pos, resize);
        }
        canvas.requestRenderAll();
    };

    let repositionFrame: number | null = null;
    let pendingOrigin: PatternElement | null = null;
    let pendingResize = false;
    const repositionReplicas = (origin: PatternElement, options: { resize?: boolean } = {}) => {
        pendingOrigin = origin;
        pendingResize = pendingResize || !!options.resize;
        if (repositionFrame !== null) return;
        repositionFrame = requestAnimationFrame(() => {
            repositionFrame = null;
            const originToSync = pendingOrigin;
            const resize = pendingResize;
            pendingOrigin = null;
            pendingResize = false;
            if (originToSync) repositionReplicasNow(originToSync, resize);
        });
    };

    const resyncReplicas = async (origin: PatternElement) => {
        const canvas = getCanvas();
        const config = origin.patternConfig;
        if (!config) return;

        const cells = enumerateCells(config);
        const replicas = TextService.getPatternReplicaObjects(canvas, origin.uuid as string);

        if (cells.length !== replicas.length) {
            await reconcileReplicas(origin, config, cells);
        } else {
            const tasks = [];
            for (let i = 0; i < cells.length; i++) {
                const pos = computeCellPosition(origin, config, cells[i]);
                replicas[i].patternCell = { row: cells[i].row, col: cells[i].col };
                replicas[i].set('zIndex', origin.zIndex + 1 + i);
                tasks.push(TextService.syncPatternReplica(canvas, origin, replicas[i], pos));
            }
            await Promise.all(tasks);
        }
        updateElementsOrderFromZIndex(canvas);
        canvas.requestRenderAll();
    };

    const refreshFill = async (origin: PatternElement) => {
        const canvas = getCanvas();
        centerOrigin(canvas, origin);

        const config: PatternConfig = { ...(origin.patternConfig || PATTERN_DEFAULTS) };
        const bounds = getArtworkBounds(canvas);
        const extent = computeFillExtent(origin, config, bounds.width, bounds.height);
        config.rows = extent.rows;
        config.cols = extent.cols;
        origin.set('patternConfig', config);
        markEdited(origin, ['patternConfig']);

        await reconcileReplicas(origin, config, enumerateCells(config));
        updateElementsOrderFromZIndex(canvas);
        canvas.requestRenderAll();
    };

    const updateConfig = (origin: PatternElement, partial: Partial<PatternConfig>) => {
        const current = origin.patternConfig || PATTERN_DEFAULTS;
        const config: PatternConfig = { ...current, ...partial };
        origin.set('patternConfig', config);
        markEdited(origin, ['patternConfig']);
        repositionReplicas(origin);
    };

    const removePattern = (origin: PatternElement) => {
        const canvas = getCanvas();
        const replicas = TextService.getPatternReplicaObjects(canvas, origin.uuid as string);
        for (const replica of replicas) canvas.remove(replica);
        ProductService.removePatternElementsLinks(origin.uuid as string);

        origin.set('pattern', false);
        origin.set('patternConfig', null);
        markEdited(origin, ['pattern', 'patternConfig']);
        updateElementsOrderFromZIndex(canvas);
        canvas.requestRenderAll();
    };

    return { createPattern, repositionReplicas, resyncReplicas, refreshFill, updateConfig, removePattern };
};
